import React, { useState, useEffect, useMemo } from "react";
import { Typography, Grid } from "@mui/material";
import CustomTable from "components/TableExplanded/Table";
import CustomPagination from "components/TableExplanded/Pagination";
import useProductData from "pages/Pawn/Products/OverdueBorrowItems/data/data";
import useColumns from "pages/Pawn/Products/OverdueBorrowItems/columns/mainColumns";
import useColumnsTwo from "pages/Pawn/Products/OverdueBorrowItems/columns/subColumns";
import Modal from "components/Modal/Pawn/Create/ModalBorrow";
import ViewModal from "components/Modal/Pawn/View/ViewOverDueBorrow";
import SearchBar from "components/TableExplanded/SearchBar";
import dayjs from "dayjs";
import api from "_api";
import { GradientButtonOutline } from "components/GradientButton";
import { Save } from "@mui/icons-material";

const OverBorrowItems = ({ className }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isViewModalOpen, setViewModalOpen] = useState(false);
  const [selectedProduct, setProduct] = useState(null);
  const [viewProduct, setViewProduct] = useState(null);
  const [fetchData, setFetchData] = useState();
  const [searchQuery, setSearchQuery] = useState(""); // Store the search query
  const [dataLength, setDataLength] = useState(0);
  const fetchProductData = async () => {
    try {
      const { data, isStatus } = await api.product.getOverDueOrders();
      if (isStatus) setFetchData(data);
    } catch (error) {
      console.error("Error fetching product data:", error);
    }
  };

  useEffect(() => {
    fetchProductData();
  }, []);

  const handleModalOpen = () => {
    setModalOpen(!isModalOpen);
    if (selectedProduct && isModalOpen) setProduct(null);
  };

  const handleViewOpen = () => {
    setViewModalOpen(!isViewModalOpen);
    if (viewProduct && isViewModalOpen) setViewProduct(null);
  };

  const data = useProductData(fetchData);
  const columns = useColumns(
    setViewProduct,
    setProduct,
    handleViewOpen,
    handleModalOpen
  );
  const columnsInside = useColumnsTwo();

  const visibleColumns = columns.filter((col) => col.showInFilter);

  const filteredData = useMemo(() => {
    if (!searchQuery) return data; // If no search query, return all data

    return data.filter((row) => {
      return visibleColumns.some((column) => {
        const cellValue = row[column.accessor]?.toString()?.toLowerCase();
        return cellValue && cellValue.includes(searchQuery.toLowerCase());
      });
    });
  }, [data, searchQuery, visibleColumns]);

  const handleSearchChange = (event) => {
    // Check if event.target and event.target.value are defined
    if (
      event &&
      event.target &&
      event.target.value !== undefined &&
      event.target.value !== null
    ) {
      setSearchTerm(event.target.value.toLowerCase());
      setSearchQuery(event.target.value);
    } else {
      console.error("Event target or value is undefined");
    }
    setPage(0);
  };

  const handleClear = () => {
    setSearchTerm("");
    setPage(0);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExportExcel = async () => {
    await api.product
      .exportExcelOverDueProducts()
      .then((res) => {
        const contentDisposition = res.headers["content-disposition"];
        let filename = "download.xlsx"; // Default กรณีไม่มีชื่อไฟล์จาก API

        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?(.+?)"?$/);
          if (match && match[1]) {
            filename = match[1];
          }
        }

        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
      })
      .catch((error) => {
        console.error("Error exporting excel:", error);
      });
  };

  return (
    <div className={className}>
      <Grid container spacing={2} style={{ marginBottom: "16px" }}>
        <Grid
          item
          xs={12}
          sm={12}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography component="div" style={{ fontSize: "32px" }}>
            เกินกำหนดยืม
          </Typography>
          <GradientButtonOutline
            onClick={handleExportExcel}
            sx={{
              px: 3,
              display: "flex",
              alignItems: "center",
              height: "48px",
            }}
          >
            <Typography style={{ marginRight: "8px" }}>ส่งออก Excel</Typography>
            <Save fontSize="medium" />
          </GradientButtonOutline>
        </Grid>
      </Grid>

      <SearchBar
        columns={columns}
        searchTerm={setSearchTerm}
        visibleColumns={visibleColumns} // กรองคอลัมน์ที่ต้องการแสดง
        handleSearchChange={handleSearchChange}
        handleClear={handleClear}
        fetchData={fetchData}
        QRCode={true}
      />

      <CustomTable
        columns={columns}
        columnsInside={columnsInside}
        searchTerm={searchTerm}
        data={filteredData}
        page={page}
        rowsPerPage={rowsPerPage}
        setDataLength={setDataLength}
      />
      <CustomPagination
        count={searchTerm ? dataLength : filteredData.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />

      <Modal
        open={isModalOpen}
        onClose={handleModalOpen}
        selectedProduct={selectedProduct}
        fetchProductData={fetchProductData}
      />

      <ViewModal
        open={isViewModalOpen}
        onClose={handleViewOpen}
        selectedProduct={viewProduct}
      />
    </div>
  );
};

export default OverBorrowItems;
